import { useState } from "react";
import { updateProfile, resetOnboarding, type UserRow, type CefrLevel } from "../lib/api";

interface Props {
  user: UserRow;
  onClose: () => void;
}

const TRACK_OPTIONS = [
  { value: "conversation", label: "Conversation" },
  { value: "work",         label: "Business / work" },
  { value: "travel",       label: "Travel" },
  { value: "exam_ielts",   label: "IELTS prep" },
  { value: "exam_toefl",   label: "TOEFL prep" },
  { value: "exam_cambridge", label: "Cambridge" },
  { value: "academic",     label: "Academic" },
  { value: "kids",         label: "Kids" },
];
const LEVELS: CefrLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
const INTEREST_CHIPS = [
  "tech", "sports", "music", "films", "books", "gaming", "food", "travel",
  "business", "science", "history", "art", "fashion", "fitness", "family",
  "politics", "cars", "nature",
];

export function SettingsView({ user, onClose }: Props): JSX.Element {
  const [name, setName] = useState(user.name ?? "");
  const [track, setTrack] = useState(user.goal_track ?? "conversation");
  const [targetLevel, setTargetLevel] = useState<CefrLevel>((user.target_level as CefrLevel) ?? "B2");
  const [profession, setProfession] = useState(user.profession ?? "");
  const [interests, setInterests] = useState<string[]>(user.interests ?? []);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggleInterest(t: string): void {
    setInterests((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : prev.length >= 8 ? prev : [...prev, t],
    );
  }

  async function save(): Promise<void> {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await updateProfile({
        name: name.trim() || undefined,
        goal_track: track,
        target_level: targetLevel,
        profession: profession.trim() || undefined,
        interests,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-5">
      <button onClick={onClose} className="text-xs text-ink-400 hover:text-white">← back to dashboard</button>

      <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5 space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Profile</h2>
        <label className="block">
          <div className="text-xs text-ink-400 mb-1">Name</div>
          <input value={name} onChange={(e) => setName(e.target.value)}
            className="w-full rounded-xl bg-ink-800 border border-ink-700 px-3 py-2 text-white focus:outline-none focus:border-lime-500" />
        </label>
        <label className="block">
          <div className="text-xs text-ink-400 mb-1">Profession</div>
          <input value={profession} onChange={(e) => setProfession(e.target.value)}
            placeholder="e.g. backend engineer"
            className="w-full rounded-xl bg-ink-800 border border-ink-700 px-3 py-2 text-white focus:outline-none focus:border-lime-500" />
        </label>
      </section>

      <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5 space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Track</h2>
        <div className="grid grid-cols-2 gap-2">
          {TRACK_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => setTrack(opt.value)}
              className={`text-left rounded-xl border px-3 py-2 transition ${track === opt.value ? "border-lime-500 bg-lime-500/10 text-white" : "border-ink-700 bg-ink-800/50 text-ink-300 hover:border-ink-600"}`}>
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5 space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Target level</h2>
        <div className="flex flex-wrap gap-2">
          {LEVELS.map((lvl) => (
            <button key={lvl} onClick={() => setTargetLevel(lvl)}
              className={`rounded-xl border px-4 py-2 transition ${targetLevel === lvl ? "border-lime-500 bg-lime-500/15 text-white" : "border-ink-700 bg-ink-800/50 text-ink-300 hover:border-ink-600"}`}>
              {lvl}
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-ink-700 bg-ink-900/60 p-5 space-y-3">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Interests (max 8)</h2>
        <div className="flex flex-wrap gap-2">
          {INTEREST_CHIPS.map((t) => {
            const on = interests.includes(t);
            return (
              <button key={t} onClick={() => toggleInterest(t)}
                className={`rounded-full border px-3 py-1.5 text-sm transition ${on ? "border-lime-500 bg-lime-500/15 text-lime-200" : "border-ink-700 bg-ink-800/50 text-ink-300 hover:border-ink-600"}`}>
                {t}
              </button>
            );
          })}
        </div>
      </section>

      <div className="flex items-center justify-between">
        <button
          onClick={async () => {
            if (!confirm("Restart onboarding from scratch? Your sessions, vocab and errors stay; only profile + level reset.")) return;
            const ok = await resetOnboarding();
            if (ok) window.location.reload();
          }}
          className="text-xs text-rose-300/80 hover:text-rose-200 underline-offset-2 hover:underline"
        >
          Reset learning profile…
        </button>
        <div className="flex items-center gap-3">
          {saved && <span className="text-emerald-300 text-xs">{"✓"} Saved.</span>}
          {error && <span className="text-rose-300 text-xs">{error}</span>}
          <button onClick={save} disabled={saving}
            className="rounded-xl bg-lime-500 hover:bg-lime-400 text-ink-900 font-semibold px-5 py-2.5 disabled:opacity-40">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
